import { Auth, getAuth } from 'firebase/auth';
import type { DataSnapshot, DatabaseReference } from 'firebase/database';
import { get, getDatabase, ref, remove, update } from 'firebase/database';
import { decodeJwt } from 'jose';
import Environment from '../../utils/Environement';
import FoxOrderApi, { Franchise, Shop } from '../api/FoxOrderApi';
import { FOXORDER_V2, StoreActionType } from '../constant/ActionConstant';
import AppDispatcher from '../dispatcher/AppDispatcher';
import Service from '../model/service/Service';

type Roles = 'ROLE_MANAGER' | 'ROLE_SHOP_MANAGER'

type Token = { roles: Roles[], franchise?: number, shop?: number }

class FoxOrderActionCreator {
    private api: FoxOrderApi

    constructor() {
        this.api = new FoxOrderApi(Environment.isDev, Environment.isPopina);
    }

    async loadService(): Promise<void> {
        const auth = getAuth()
        const userUid = auth.currentUser?.uid
        if (userUid === undefined) {
            throw new Error("error firebase not connected");
        }
        try {
            const db = getDatabase()
            const reference: DatabaseReference = ref(db, `thirdpartys/${userUid}/foxorderv2/`)
            const snapshot: DataSnapshot = await get(reference)
            const foxOrderService = snapshot.val()

            if (snapshot.exists()) {
                const serviceUser = new Service(foxOrderService)
                AppDispatcher.dispatch({
                    'type': StoreActionType.SET_FOXORDER_V2_SERVICE,
                    'data': serviceUser
                })
            }

        } catch (error) {
            if (error instanceof Error) {
                throw error.message
            }
            throw error
        }
    }

    async decodeJWT(token: string, userUid: string): Promise<Franchise | Shop> {
        const decodedToken = decodeJwt(token)
        if (!decodedToken) {
            throw new Error('Error while decoding JWT token')
        }

        const tokenData: Token = decodedToken as Token;

        return await this.retrieveEstablishmentType(tokenData, token, userUid)
    }

    async retrieveEstablishmentType(tokenData: Token, token: string, userUid: string): Promise<Franchise | Shop> {
        if (tokenData.roles.includes('ROLE_MANAGER') && tokenData.franchise) {
            return await this.api.getFranchiseFromID(tokenData.franchise, token)
        } else if (tokenData.roles.includes('ROLE_SHOP_MANAGER') && tokenData.shop) {
            return await this.api.getShopFromID(tokenData.shop, token)
        }

        throw new Error('Unknown role for retrieving shop id')
    }


    async connectService(username: string, password: string, franchise: number): Promise<void | string> {
        const auth: Auth = getAuth()
        const userUid: string | undefined = auth.currentUser?.uid
        if (userUid === undefined) {
            throw new Error("error firebase not connected");
        }

        try {
            const test = await this.api.authenticateFoxOrderUser(username, password, franchise, userUid)
                .then(async (response) => {
                    let serviceUser: Service
                    const franchiseOrShop = await this.decodeJWT(response.token, userUid)
                    const database = getDatabase()
                    const mapped = await this.isAlreadlyMappedToAnotherAccount(franchiseOrShop)
                    if (mapped) {
                        return mapped
                    }
                    const thirdPartysReference = ref(database, `thirdpartys/${userUid}/foxorderv2/`)

                    serviceUser = new Service({
                        service: FOXORDER_V2,
                        accessToken: response.token,
                        refreshToken: response.refresh_token,
                        isActive: false,
                        uid: userUid,
                    })


                    if ('franchise' in franchiseOrShop) {
                        serviceUser = new Service({
                            service: FOXORDER_V2,
                            accessToken: response.token,
                            refreshToken: response.refresh_token,
                            isActive: true,
                            uid: userUid,
                            accountName: franchiseOrShop.name,
                            accountId: `${franchiseOrShop.id}`,
                            shopToken: franchiseOrShop.token,
                            franchiseToken: franchiseOrShop.franchise?.token,
                        })

                        this.mapShopIdAndUserUid(userUid, franchiseOrShop);

                        await update(thirdPartysReference, serviceUser.toJS())

                        AppDispatcher.dispatch({
                            'type': StoreActionType.SET_FOXORDER_V2_SERVICE,
                            'data': serviceUser,
                        })
                    } else {
                        if ('shops' in franchiseOrShop && franchiseOrShop.shops.length === 1) {
                            const shop = franchiseOrShop?.shops[0]
                            serviceUser = new Service({
                                service: FOXORDER_V2,
                                accessToken: response.token,
                                refreshToken: response.refresh_token,
                                isActive: true,
                                uid: userUid,
                                accountId: `${shop.id}`,
                                accountName: shop.name,
                                franchiseToken: franchiseOrShop.token,
                                shopToken: franchiseOrShop.shops[0].token,
                            })

                            await update(thirdPartysReference, serviceUser.toJS())

                            this.mapShopIdAndUserUid(userUid, shop);

                            AppDispatcher.dispatch({
                                'type': StoreActionType.SET_FOXORDER_V2_SERVICE,
                                'data': serviceUser,
                            })

                        } else if ('shops' in franchiseOrShop && franchiseOrShop.shops.length > 1) {
                            AppDispatcher.dispatch({
                                'type': StoreActionType.SET_FOXORDER_V2_SHOPS,
                                'data': franchiseOrShop?.shops,
                            });
                        }
                    }


                }).catch((error) => {
                    console.log(error)
                    throw new Error('Error while authenticating FoxOrder user')
                })
            if (test) {
                return test
            }
        } catch (error) {
            if (error instanceof Error) {
                throw error.message
            }
            throw error

        }

    }

    async selectEstablishment(establishment: Shop) {
        const auth: Auth = getAuth()
        const userUid: string | undefined = auth.currentUser?.uid
        if (userUid === undefined) {
            throw new Error("error firebase not connected");
        }
        try {
            const database = getDatabase()
            const thirdPartysReference = ref(database, `thirdpartys/${userUid}/foxorderv2/`)
            const serviceUser = new Service({
                service: FOXORDER_V2,
                isActive: true,
                uid: userUid,
                accountId: `${establishment.id}`,
                accountName: establishment.name,
            })

            await update(thirdPartysReference, serviceUser.toJS())

            this.mapShopIdAndUserUid(userUid, establishment);

            AppDispatcher.dispatch({
                'type': StoreActionType.SET_FOXORDER_V2_SERVICE,
                'data': serviceUser,
            })
        } catch (error) {
            if (error instanceof Error) {
                throw error.message
            }
            throw error
        }
    }

    async mapShopIdAndUserUid(userUid: string, establishment: Shop) {
        try {
            const database = getDatabase()
            const extIdsReference = ref(database, 'ext_ids')
            await update(extIdsReference, { [establishment.id]: userUid });
        } catch (error) {
            if (error instanceof Error) {
                throw error.message
            }
            throw error
        }
    }

    /**
     * Check if the shop is already connected to another user
     * @param shopId - Fox order shop id
     * @returns boolean - true if the shop is already connected to another user
     */
    async isAlreadlyMappedToAnotherAccount(franchiseOrShop: Shop | Franchise): Promise<string | void> {
        try {
            const database = getDatabase()
            const extIdsReference = ref(database, 'ext_ids/')
            const snapshot = await get(extIdsReference)
            const extIds = snapshot.val()

            if (!extIds) {
                throw new Error('Error while fetching extIds')
            }

            if ('franchise' in franchiseOrShop) {
                if (extIds[franchiseOrShop.id.toString()]) {
                    return `franchise is already connected to another user`
                }
            }

            if ('shops' in franchiseOrShop && franchiseOrShop.shops.length === 1) {
                if (extIds[franchiseOrShop.shops[0].id.toString()]) {
                    return `Shop is already connected to another user`
                }
            }
        } catch (error) {
            if (error instanceof Error) {
                throw error.message
            }
            throw error
        }
    }

    async disconnectFoxOrderService(shopId: string) {
        const auth: Auth = getAuth()
        const userUid: string | undefined = auth.currentUser?.uid
        if (userUid === undefined) {
            throw new Error("error firebase not connected");
        }
        try {
            const database = getDatabase()
            const thirdPartysReference = ref(database, `thirdpartys/${userUid}/foxorderv2/`)
            await remove(thirdPartysReference)
            const extIdsReference = ref(database, 'ext_ids')
            await update(extIdsReference, { [shopId]: null });
            AppDispatcher.dispatch({
                'type': StoreActionType.SET_FOXORDER_V2_SERVICE,
                'data': new Service({ service: FOXORDER_V2, accessToken: '', isActive: false, uid: userUid })
            })
        } catch (error) {
            if (error instanceof Error) {
                throw error.message
            }
            throw error
        }
    }

    async getAllActiveConnections() {
        const database = getDatabase()
        const thirdPartysReference = ref(database, `thirdpartys/`)

        // const clientsWithFoxOrder: any[] = []

        get(thirdPartysReference).then((snapshot) => {
            for (const userUid in snapshot.val()) {
                if(snapshot.val()[userUid].foxorderv2) {
                    console.log(userUid, snapshot.val()[userUid].foxorderv2)
                    // clientsWithFoxOrder.push(snapshot.val()[userUid].foxorderv2)
                }
            }
        })
        // return clientsWithFoxOrder
    }
}

const foxOrderActionCreator = new FoxOrderActionCreator();
export default foxOrderActionCreator;